// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Document Requirements
// Configure FEMA documentation checklists for each invoice type.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea,
  Select, Tabs, Stack, Checkbox, Alert,
} from '../../components/gab/GabComponents';
import { ActionIcon } from '../../components/gab/GabComponents';
import {
  IconGripVertical, IconTrash, IconPlus, IconCheck,
  IconArrowUp, IconArrowDown, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { fetchRecords, createRecord, updateRecord } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { DEFAULT_TEMPLATES } from '../../data/checklistDefaults';
import type { ChecklistTemplate, ChecklistTemplateItem, InvoiceType } from '../../types/checklist';
import { INVOICE_TYPE_LABELS } from '../../types/checklist';

const FT = FIELDS.debrisChecklistTemplates;

const INVOICE_TYPES: InvoiceType[] = [
  'debris',
  'non_debris_contractor',
  'force_account_labor',
  'force_account_equipment',
];

const EVIDENCE_OPTIONS = [
  { value: 'file', label: 'File Upload' },
  { value: 'manual', label: 'Manual Confirm' },
];

// Map invoiceType → saved record id (if any)
type SavedMap = Record<InvoiceType, number | null>;

function newItemId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function EvidenceBadge({ type }: { type: string }) {
  if (type === 'auto') {
    return (
      <Badge color="gray" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Auto
      </Badge>
    );
  }
  if (type === 'file') {
    return (
      <Badge color="blue" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        File
      </Badge>
    );
  }
  return (
    <Badge color="teal" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      Manual
    </Badge>
  );
}

interface ItemRowProps {
  item: ChecklistTemplateItem;
  index: number;
  total: number;
  onChange: (updated: ChecklistTemplateItem) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function ItemRow({ item, index, total, onChange, onDelete, onMoveUp, onMoveDown }: ItemRowProps) {
  const [descOpen, setDescOpen] = useState(false);

  return (
    <Paper
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '10px 12px',
        background: '#fff',
      }}
    >
      <Group style={{ alignItems: 'flex-start', gap: 8 }}>
        {/* Drag handle / order controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 6 }}>
          <ActionIcon
            size="xs"
            variant="subtle"
            disabled={index === 0}
            onClick={onMoveUp}
            title="Move up"
            style={{ color: '#94a3b8', padding: 2 }}
          >
            <IconArrowUp size={13} />
          </ActionIcon>
          <span style={{ color: '#cbd5e1', display: 'flex', justifyContent: 'center' }}>
            <IconGripVertical size={14} />
          </span>
          <ActionIcon
            size="xs"
            variant="subtle"
            disabled={index === total - 1}
            onClick={onMoveDown}
            title="Move down"
            style={{ color: '#94a3b8', padding: 2 }}
          >
            <IconArrowDown size={13} />
          </ActionIcon>
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group style={{ alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <EvidenceBadge type={item.evidenceType} />
            {item.required && (
              <Badge color="red" style={{ fontSize: 10 }}>Required</Badge>
            )}
            <span
              style={{ fontSize: 11, color: '#94a3b8', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setDescOpen(o => !o)}
            >
              {descOpen ? '▲ hide desc' : '▼ desc'}
            </span>
          </Group>

          <TextInput
            value={item.label}
            onChange={e => onChange({ ...item, label: e.target.value })}
            placeholder="Item label"
            style={{ marginBottom: 6 }}
          />

          {descOpen && (
            <Textarea
              value={item.description}
              onChange={e => onChange({ ...item, description: e.target.value })}
              placeholder="Description visible to reviewers"
              rows={2}
              style={{ marginBottom: 6 }}
            />
          )}

          {item.evidenceType === 'auto' && item.autoSource && (
            <Text size="xs" style={{ color: '#64748b', fontStyle: 'italic', marginTop: 2 }}>
              Auto-check: {item.autoSource}
            </Text>
          )}

          {item.evidenceType !== 'auto' && (
            <Group style={{ alignItems: 'center', gap: 12, marginTop: 4 }}>
              <Select
                value={item.evidenceType}
                onChange={val => onChange({ ...item, evidenceType: (val ?? 'manual') as 'file' | 'manual' })}
                data={EVIDENCE_OPTIONS}
                style={{ width: 160 }}
                size="xs"
              />
              <Checkbox
                checked={item.required}
                onChange={e => onChange({ ...item, required: e.target.checked })}
                label="Required"
                size="sm"
              />
            </Group>
          )}

          {item.evidenceType === 'auto' && (
            <Group style={{ alignItems: 'center', gap: 12, marginTop: 4 }}>
              <Checkbox
                checked={item.required}
                onChange={e => onChange({ ...item, required: e.target.checked })}
                label="Required"
                size="sm"
              />
            </Group>
          )}
        </div>

        {/* Delete (only non-auto) */}
        <div style={{ paddingTop: 4 }}>
          {item.evidenceType !== 'auto' ? (
            <ActionIcon
              size="sm"
              variant="subtle"
              color="red"
              onClick={onDelete}
              title="Delete item"
              style={{ color: 'var(--color-error)' }}
            >
              <IconTrash size={15} />
            </ActionIcon>
          ) : (
            <div style={{ width: 28 }} />
          )}
        </div>
      </Group>
    </Paper>
  );
}

interface TabPanelProps {
  invoiceType: InvoiceType;
  items: ChecklistTemplateItem[];
  saving: boolean;
  onItemsChange: (items: ChecklistTemplateItem[]) => void;
  onSave: () => void;
  onReset: () => void;
}

function TemplateTabPanel({ invoiceType, items, saving, onItemsChange, onSave, onReset }: TabPanelProps) {
  function handleChange(index: number, updated: ChecklistTemplateItem) {
    const next = [...items];
    next[index] = updated;
    onItemsChange(next);
  }

  function handleDelete(index: number) {
    onItemsChange(items.filter((_, i) => i !== index));
  }

  function handleMoveUp(index: number) {
    if (index === 0) return;
    const next = [...items];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onItemsChange(next.map((it, i) => ({ ...it, order: i + 1 })));
  }

  function handleMoveDown(index: number) {
    if (index === items.length - 1) return;
    const next = [...items];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    onItemsChange(next.map((it, i) => ({ ...it, order: i + 1 })));
  }

  function handleAddItem() {
    const newItem: ChecklistTemplateItem = {
      id: newItemId(),
      label: '',
      description: '',
      required: false,
      evidenceType: 'manual',
      order: items.length + 1,
    };
    onItemsChange([...items, newItem]);
  }

  const autoCount = items.filter(it => it.evidenceType === 'auto').length;
  const fileCount = items.filter(it => it.evidenceType === 'file').length;
  const manualCount = items.filter(it => it.evidenceType === 'manual').length;
  const requiredCount = items.filter(it => it.required).length;

  return (
    <Stack gap={12} style={{ paddingTop: 12 }}>
      {/* Stats row */}
      <Group style={{ gap: 8 }}>
        <Badge color="gray">{items.length} items</Badge>
        <Badge color="red">{requiredCount} required</Badge>
        <Badge color="gray">{autoCount} auto</Badge>
        <Badge color="blue">{fileCount} file</Badge>
        <Badge color="teal">{manualCount} manual</Badge>
      </Group>

      <Alert
        color="blue"
        title="Auto-check items"
        style={{ fontSize: 13 }}
      >
        Items marked <strong>Auto</strong> are computed by the system at review time. Their evidence type
        cannot be changed, but you can toggle Required and edit the label.
      </Alert>

      {/* Item rows */}
      <Stack gap={8}>
        {items.map((item, idx) => (
          <ItemRow
            key={item.id}
            item={item}
            index={idx}
            total={items.length}
            onChange={updated => handleChange(idx, updated)}
            onDelete={() => handleDelete(idx)}
            onMoveUp={() => handleMoveUp(idx)}
            onMoveDown={() => handleMoveDown(idx)}
          />
        ))}
      </Stack>

      {/* Add item */}
      <Button
        variant="outline"
        leftSection={<IconPlus size={15} />}
        onClick={handleAddItem}
        style={{ alignSelf: 'flex-start' }}
      >
        Add Item
      </Button>

      {/* Save / Reset */}
      <Group style={{ gap: 8, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
        <Button
          leftSection={<IconCheck size={15} />}
          onClick={onSave}
          loading={saving}
          color="blue"
        >
          Save Template
        </Button>
        <Button
          variant="subtle"
          leftSection={<IconRefresh size={15} />}
          onClick={onReset}
          color="gray"
        >
          Reset to Defaults
        </Button>
      </Group>
    </Stack>
  );
}

export default function DocumentRequirements() {
  const [activeTab, setActiveTab] = useState<string>('debris');
  // templates keyed by invoiceType
  const [templates, setTemplates] = useState<Record<InvoiceType, ChecklistTemplateItem[]>>(() => {
    const init: Partial<Record<InvoiceType, ChecklistTemplateItem[]>> = {};
    for (const t of DEFAULT_TEMPLATES) {
      init[t.invoiceType] = t.items;
    }
    return init as Record<InvoiceType, ChecklistTemplateItem[]>;
  });
  const [savedMap, setSavedMap] = useState<SavedMap>({
    debris: null,
    non_debris_contractor: null,
    force_account_labor: null,
    force_account_equipment: null,
  });
  const [saving, setSaving] = useState<InvoiceType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplates();
  }, []);

  async function loadTemplates() {
    setLoading(true);
    try {
      const res = await fetchRecords<Record<string, unknown>>(
        TABLES.debrisChecklistTemplates,
        { limit: 50 },
      );
      const newSavedMap: SavedMap = {
        debris: null,
        non_debris_contractor: null,
        force_account_labor: null,
        force_account_equipment: null,
      };
      const newTemplates = { ...templates };

      for (const row of res.records) {
        const type = row[FT.invoiceType] as InvoiceType | undefined;
        const configStr = row[FT.config] as string | undefined;
        if (!type || !INVOICE_TYPES.includes(type)) continue;

        newSavedMap[type] = row.record_id as number;

        if (configStr) {
          try {
            const parsed = JSON.parse(configStr) as { items?: ChecklistTemplateItem[] };
            if (parsed.items && Array.isArray(parsed.items)) {
              newTemplates[type] = parsed.items;
            }
          } catch {
            // ignore parse errors, keep defaults
          }
        }
      }

      setSavedMap(newSavedMap);
      setTemplates(newTemplates);
    } catch (err) {
      notifications.show({
        title: 'Load error',
        message: 'Could not load saved templates — showing defaults.',
        color: 'orange',
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(invoiceType: InvoiceType) {
    setSaving(invoiceType);
    const items = templates[invoiceType];
    const config = JSON.stringify({ items });
    const payload: Record<string, unknown> = {
      [FT.invoiceType]: invoiceType,
      [FT.config]: config,
      [FT.updatedAt]: new Date().toISOString(),
    };

    try {
      const existingId = savedMap[invoiceType];
      if (existingId !== null) {
        await updateRecord(TABLES.debrisChecklistTemplates, existingId, payload);
      } else {
        const created = await createRecord<Record<string, unknown>>(
          TABLES.debrisChecklistTemplates,
          payload,
        );
        setSavedMap(prev => ({
          ...prev,
          [invoiceType]: created.record_id as number,
        }));
      }
      notifications.show({
        title: 'Saved',
        message: `${INVOICE_TYPE_LABELS[invoiceType]} checklist saved.`,
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: String(err),
        color: 'red',
      });
    } finally {
      setSaving(null);
    }
  }

  function handleReset(invoiceType: InvoiceType) {
    const def = DEFAULT_TEMPLATES.find(t => t.invoiceType === invoiceType);
    if (!def) return;
    setTemplates(prev => ({ ...prev, [invoiceType]: def.items }));
    notifications.show({
      title: 'Reset',
      message: 'Template reset to defaults. Save to persist.',
      color: 'blue',
    });
  }

  function handleItemsChange(invoiceType: InvoiceType, items: ChecklistTemplateItem[]) {
    setTemplates(prev => ({ ...prev, [invoiceType]: items }));
  }

  return (
    <div style={{ marginTop: 24 }}>
      <Stack gap={12}>
        <div>
          <Title order={3} style={{ fontWeight: 700, color: '#1e293b' }}>
            Document Requirements
          </Title>
          <Text size="sm" style={{ color: '#64748b', marginTop: 2 }}>
            Configure FEMA documentation checklists for each invoice type.
          </Text>
        </div>

        {loading ? (
          <Paper style={{ padding: 32, textAlign: 'center' }}>
            <Text size="sm" style={{ color: '#94a3b8' }}>Loading saved templates…</Text>
          </Paper>
        ) : (
          <Paper style={{ padding: 16, border: '1px solid #e2e8f0', borderRadius: 10 }}>
            <Tabs value={activeTab} onChange={v => setActiveTab(v ?? 'debris')}>
              <Tabs.List>
                {INVOICE_TYPES.map(type => (
                  <Tabs.Tab key={type} value={type}>
                    <Group style={{ gap: 6, alignItems: 'center' }}>
                      {INVOICE_TYPE_LABELS[type]}
                      {savedMap[type] !== null && (
                        <IconCheck size={12} style={{ color: 'var(--color-success)' }} />
                      )}
                    </Group>
                  </Tabs.Tab>
                ))}
              </Tabs.List>

              {INVOICE_TYPES.map(type => (
                <Tabs.Panel key={type} value={type}>
                  <TemplateTabPanel
                    invoiceType={type}
                    items={templates[type]}
                    saving={saving === type}
                    onItemsChange={items => handleItemsChange(type, items)}
                    onSave={() => handleSave(type)}
                    onReset={() => handleReset(type)}
                  />
                </Tabs.Panel>
              ))}
            </Tabs>
          </Paper>
        )}
      </Stack>
    </div>
  );
}
